import { APP_INITIALIZER, Injector, Provider, inject } from '@angular/core';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import {
  AccountInfo,
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalService,
} from '@azure/msal-angular';
import { catchError, firstValueFrom, of, switchMap, tap } from 'rxjs';
import { environment } from '@ups/xplat/core';
import {
  Auth0AuthenticationService,
  AuthenticationService,
} from '@ups/security';
import {
  LoginIssuerStore,
  accountCountSafe,
  selectLoginIssuer,
} from '../login-issuer.store';
import { EntraAuthenticationService } from './entra-authentication.service';
import { provideIssuerAwareHttpAuth } from '../issuer-aware-auth.providers';
import { LoginService } from '../login.service';
import { EntraLoginService } from './entra-login.service';

// Kept as re-exports so existing imports (specs, app wiring) stay valid;
// the rules themselves moved to login-issuer.store.ts (#29709 / WI 31092,
// cycle-free imports: AuthFacade consumes accountCountSafe and cannot import
// this file without a providers -> interceptor -> facade cycle).
export { selectLoginIssuer, accountCountSafe } from '../login-issuer.store';

/**
 * Completes a processed MSAL redirect: activate the account, persist the
 * issuer marker, and, when the marker did not already say 'entra', reboot
 * the document.
 *
 * The reboot is the load-bearing part (#30531, second half). The issuer
 * snapshots that route the whole session — the `AuthenticationService`
 * binding below, `AuthFacade.issuer` — are once-per-boot reads, and eager
 * root services take them at module construction (`SecurityService` injects
 * `AuthenticationService` in its field initializers), BEFORE this
 * initializer has processed the redirect response. On the redirect-return
 * boot the marker and the MSAL account cache are both still empty at
 * snapshot time, so the boot locks to Auth0: `isAuthenticated$` first-emits
 * false, the login page's forward-authenticated check reads that false, and
 * the freshly signed-in user strands on the "Welcome back" card holding a
 * fully valid MSAL session (every SUBSEQUENT boot reads the persisted
 * marker and works, which is why a manual refresh "fixes" it). Reordering
 * the snapshots after redirect processing cannot be done locally — the
 * consumers are eager by design — so the fix is to reboot once, restoring
 * the invariant the snapshot rule was written against: "redirect logins
 * reboot the app, so once-per-boot selection is exact."
 *
 * The reload is guarded, not unconditional: a boot whose marker already
 * said 'entra' snapshotted the right issuer (returning-user logins,
 * `acquireTokenRedirect` renewal returns), and rebooting those would double
 * the page loads for no correction. A null result is an ordinary boot with
 * no redirect pending — nothing is written, so no reload loop is possible
 * (the reloaded boot's `handleRedirectObservable` yields null).
 */
export function completeEntraRedirectBoot(
  result: { account: AccountInfo | null } | null,
  instance: IPublicClientApplication,
  issuerStore: LoginIssuerStore,
  reload: () => void
): void {
  if (!result?.account) {
    return;
  }
  instance.setActiveAccount(result.account);
  const priorMarker = issuerStore.get();
  issuerStore.set('entra');
  // WI 31157: remember WHO completed the redirect, in the same on-success
  // hook as the marker (the #30531 write-on-success invariant covers both).
  // The returning-user login rides this as loginHint so Entra SSOs silently
  // instead of forcing an account picker on every session start.
  issuerStore.setHint(result.account.username);
  if (priorMarker !== 'entra') {
    reload();
  }
}

export interface EntraMsalProviderOptions {
  /**
   * API URL prefixes MSAL should attach Entra tokens to, mapped to the
   * scopes each needs. Keep this to the app's Entra-audience APIs — the
   * Auth0 interceptor chain (MyHttpClientFactory) handles everything else.
   */
  protectedResources?: Array<[urlPattern: string, scopes: string[] | null]>;
  /** Scopes the guard requests on redirect. Defaults to `<audience>/.default`. */
  scopes?: string[];
  /** Defaults to the current origin. */
  redirectUri?: string;
  /**
   * When true, binds {@link AuthenticationService} to an issuer-aware
   * selection at boot: Entra when it was the last-used issuer (or MSAL
   * already holds a session), the app's existing Auth0 binding otherwise.
   * Redirect logins reboot the app, so once-per-boot selection is exact.
   * Opt-in so apps that only want the login screen keep their current
   * binding untouched.
   */
  bindAuthenticationService?: boolean;
  /**
   * When true, overrides the app's HTTP_AUTH_INTERCEPTOR binding with the
   * issuer-aware interceptor so MyHttpClientFactory clients attach the
   * active issuer's token (#29709): Entra sessions get a facade-minted
   * Bearer, Auth0 sessions delegate to the stock Auth0 interceptor
   * unchanged. Opt-in for the same reason as bindAuthenticationService;
   * apps setting this should set that too so the facade's bound
   * AuthenticationService matches the issuer.
   */
  bindHttpAuthInterceptor?: boolean;

  bindLoginService?: boolean;
}

function defaultScopes(): string[] {
  const audience = environment.entraId?.audience;
  return audience ? [`${audience}/.default`] : [];
}

export function entraMsalInstanceFactory(
  redirectUri?: string
): IPublicClientApplication {
  const uri =
    redirectUri ??
    (typeof window !== 'undefined' ? window.location.origin : '/');
  return new PublicClientApplication({
    auth: {
      clientId: environment.entraId?.clientId ?? '',
      authority: environment.entraId?.authority,
      redirectUri: uri,
      postLogoutRedirectUri: uri,
    },
    // LocalStorage, not SessionStorage (WI 31092): sessionStorage is per-tab,
    // so every new tab/window booted with an empty token cache and went
    // interactive -- web-software-security-spa alone measured 70 interactive
    // sign-ins/7d for one user. localStorage shares the cache across tabs and
    // restarts. Safe only because every pre-initialize account read below is
    // throw-guarded (accountCountSafe): msal-browser v4+ throws
    // uninitialized_public_client_application on pre-init reads, and a
    // PERSISTED cache is exactly the state where eager readers find accounts.
    cache: { cacheLocation: BrowserCacheLocation.LocalStorage },
    system: {
      allowPlatformBroker: false,
      loggerOptions: {
        logLevel: LogLevel.Warning,
        piiLoggingEnabled: false,
        loggerCallback: () => {
          /* intentionally quiet; MSAL warnings surface via redirect errors */
        },
      },
    },
  });
}

/**
 * App-agnostic version of project-hub's msalProviders: everything is
 * driven by `environment.entraId` (the `EntraId:*` Azure App Config keys)
 * plus per-app options, so any web app can register Entra login by
 * spreading this into its module/config providers. Safe to spread in an
 * app whose EntraId keys are absent: it returns NO providers, because
 * `new PublicClientApplication({ clientId: '' })` throws at construction,
 * so registering MSAL without a clientId would crash the first injector
 * that touches MsalService (even an optional inject constructs the
 * registered provider). With nothing registered, optional injection
 * resolves null and the login screen routes everyone to Auth0.
 * `environment` values are build-time constants (DefinePlugin), so this
 * decision is static per app build — provisioning the App Config key
 * turns Entra on at the next build with no code change.
 */
export function buildEntraMsalProviders(
  options: EntraMsalProviderOptions = {}
): Provider[] {
  if (!environment.entraId?.clientId || !environment.entraId?.authority) {
    return [];
  }
  const scopes = options.scopes ?? defaultScopes();

  // No forced prompt (WI 31092): prompt=select_account made EVERY guarded
  // load interactive even with a live Entra session cookie. Without it the
  // redirect completes silently (SSO); Entra shows its own picker whenever
  // more than one account is live, so multi-account users lose nothing.
  const guardConfig = (): MsalGuardConfiguration => ({
    interactionType: InteractionType.Redirect,
    authRequest: { scopes },
  });

  const interceptorConfig = (): MsalInterceptorConfiguration => ({
    interactionType: InteractionType.Redirect,
    protectedResourceMap: new Map<string, string[] | null>(
      options.protectedResources ?? []
    ),
    strictMatching: false,
  });

  return [
    {
      provide: MSAL_INSTANCE,
      useFactory: () => entraMsalInstanceFactory(options.redirectUri),
    },
    { provide: MSAL_GUARD_CONFIG, useFactory: guardConfig },
    { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: interceptorConfig },
    { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    // Process the MSAL redirect return on every boot. Returning from
    // Microsoft lands on redirectUri with the auth response in the hash;
    // nothing consumes it unless handleRedirectObservable runs. On success
    // the account becomes active and the issuer choice is persisted so the
    // next boot binds the Entra AuthenticationService. Errors are logged
    // and swallowed — a failed redirect must not brick app startup.
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: () => {
        const msal = inject(MsalService);
        const issuerStore = inject(LoginIssuerStore);
        return () =>
          firstValueFrom(
            msal.initialize().pipe(
              switchMap(() => msal.handleRedirectObservable()),
              tap((result) =>
                completeEntraRedirectBoot(
                  result,
                  msal.instance,
                  issuerStore,
                  () => window.location.reload()
                )
              ),
              catchError((err) => {
                console.error('[entra] redirect processing failed', err);
                return of(null);
              })
            )
          );
      },
    },
    ...(options.bindHttpAuthInterceptor ? provideIssuerAwareHttpAuth() : []),
    ...(options.bindAuthenticationService
      ? [
          {
            provide: AuthenticationService,
            useFactory: () => {
              const issuerStore = inject(LoginIssuerStore);
              const injector = inject(Injector);
              const issuer = selectLoginIssuer(
                issuerStore.get(),
                accountCountSafe(injector.get(MSAL_INSTANCE))
              );
              return issuer === 'entra'
                ? injector.get(EntraAuthenticationService)
                : injector.get(Auth0AuthenticationService);
            },
          } satisfies Provider,
        ]
      : []),
    ...(options.bindLoginService
      ? [{ provide: LoginService, useClass: EntraLoginService }]
      : []),
  ];
}
